<template>
  <div class="panel-card full-height" :class="{ loading: loading }">
    <div class="loading-overlay" v-if="loading"></div>
    <div class="page-header">
      <h3 class="page-header-title">部门管理</h3>
      <button class="btn p xs" @click="openCreateDialog(null)">
        <span>新增部门</span>
      </button>
    </div>

    <div class="table-shell full-height">
      <el-table
        v-if="treeList.length"
        :data="treeList"
        row-key="id"
        border
        :tree-props="{ children: 'children' }"
        height="100%"
      >
        <el-table-column prop="name" label="部门名称" min-width="220"></el-table-column>
        <el-table-column label="绑定人员" min-width="320">
          <template slot-scope="{ row }">
            <span v-if="!row.personnel_items || row.personnel_items.length === 0">-</span>
            <template v-else>
              <el-tag
                v-for="item in row.personnel_items.slice(0, 3)"
                :key="`${row.id}_${item.id}`"
                size="mini"
                class="person-tag"
              >
                {{ item.nickname || item.wechat_id || `#${item.id}` }}
              </el-tag>
              <span v-if="row.personnel_items.length > 3" class="person-more">
                +{{ row.personnel_items.length - 3 }}
              </span>
            </template>
          </template>
        </el-table-column>
        <el-table-column prop="sort" label="排序" width="80"></el-table-column>
        <el-table-column label="操作" width="270">
          <template slot-scope="{ row }">
            <span class="op-actions">
              <button class="btn s xs" @click="openCreateDialog(row.id)">新增下级</button>
              <button class="btn e xs" @click="openEditDialog(row)">编辑</button>
              <button class="btn w xs" @click="removeDepartment(row)">删除</button>
            </span>
          </template>
        </el-table-column>
      </el-table>
      <div v-else class="empty-hint">
        暂无部门数据，点击上方“新增部门”开始配置。
      </div>
    </div>

    <el-dialog
      :title="dialogMode === 'create' ? '新增部门' : '编辑部门'"
      :visible.sync="dialogVisible"
      width="520px"
      :append-to-body="true"
      :lock-scroll="true"
    >
      <el-form :model="form" label-width="92px">
        <el-form-item label="部门名称">
          <el-input v-model.trim="form.name" maxlength="100" placeholder="请输入部门名称"></el-input>
        </el-form-item>
        <el-form-item label="上级部门">
          <el-select v-model="form.parent_id" clearable placeholder="请选择上级部门">
            <el-option :value="null" label="顶级部门"></el-option>
            <el-option
              v-for="item in parentOptions"
              :key="item.id"
              :label="item.label"
              :value="item.id"
              :disabled="item.disabled"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sort" :min="-9999" :max="9999"></el-input-number>
        </el-form-item>
        <el-form-item label="绑定人员">
          <el-select
            v-model="form.personnel_ids"
            multiple
            filterable
            collapse-tags
            :loading="personnelOptionsLoading"
            placeholder="请选择人员"
          >
            <el-option
              v-for="item in personnelOptions"
              :key="item.id"
              :label="personnelOptionLabel(item)"
              :value="item.id"
            ></el-option>
          </el-select>
          <div class="field-tips">仅可选择有设备标识的人员（device_id/device_token/token_id）</div>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="dialogLoading" @click="submitDialog">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { departmentsApi } from "../../api";

export default {
  data() {
    return {
      loading: false,
      dialogLoading: false,
      personnelOptionsLoading: false,
      personnelOptionsLoaded: false,
      treeList: [],
      flatList: [],
      personnelOptions: [],
      dialogVisible: false,
      dialogMode: "create",
      currentEditId: null,
      form: {
        name: "",
        parent_id: null,
        sort: 0,
        personnel_ids: []
      }
    };
  },
  computed: {
    parentOptions() {
      const editingId = Number(this.currentEditId || 0);
      const disabledSet = editingId > 0 ? this.collectDescendantIds(editingId) : new Set();
      if (editingId > 0) {
        disabledSet.add(editingId);
      }

      return this.flatList.map((item) => {
        const level = Number(item.level || 0);
        const prefix = level > 0 ? `${"　".repeat(level)}└ ` : "";
        return {
          id: Number(item.id),
          label: `${prefix}${item.name || `部门#${item.id}`}`,
          disabled: disabledSet.has(Number(item.id))
        };
      });
    }
  },
  created() {
    this.loadDepartmentTree();
  },
  methods: {
    async loadDepartmentTree() {
      this.loading = true;
      try {
        const departmentData = await departmentsApi.getDepartmentTree();
        const rawTree = Array.isArray(departmentData?.items) ? departmentData.items : [];
        this.treeList = this.normalizeTree(rawTree);
        this.flatList = this.flattenTree(this.treeList);
      } catch (e) {
        this.$message.error("加载部门管理数据失败");
      } finally {
        this.loading = false;
      }
    },
    async ensurePersonnelOptionsLoaded(force = false) {
      if (this.personnelOptionsLoading) return;
      if (this.personnelOptionsLoaded && !force) return;

      this.personnelOptionsLoading = true;
      try {
        const personnelData = await departmentsApi.getDepartmentPersonnelOptions();
        const options = Array.isArray(personnelData?.items) ? personnelData.items : [];
        this.personnelOptions = options.map((item) => ({
          id: Number(item?.id || 0),
          nickname: String(item?.nickname || ""),
          wechat_id: String(item?.wechat_id || ""),
          device_id: String(item?.device_id || "")
        })).filter((item) => item.id > 0);
        this.personnelOptionsLoaded = true;
      } catch (e) {
        this.$message.error("加载人员选项失败");
      } finally {
        this.personnelOptionsLoading = false;
      }
    },
    normalizeTree(list = []) {
      return (Array.isArray(list) ? list : []).map((item) => ({
        id: Number(item?.id || 0),
        parent_id: item?.parent_id === null || item?.parent_id === undefined || Number(item?.parent_id) === 0
          ? null
          : Number(item.parent_id),
        name: String(item?.name || ""),
        sort: Number(item?.sort || 0),
        personnel_ids: Array.isArray(item?.personnel_ids)
          ? item.personnel_ids.map((id) => Number(id)).filter((id) => id > 0)
          : [],
        personnel_items: Array.isArray(item?.personnel_items) ? item.personnel_items : [],
        children: this.normalizeTree(item?.children || [])
      }));
    },
    flattenTree(tree = []) {
      const rows = [];
      const walk = (nodes, parentId = null, level = 0) => {
        (Array.isArray(nodes) ? nodes : []).forEach((node) => {
          const id = Number(node?.id || 0);
          if (id <= 0) return;
          rows.push({
            id,
            parent_id: parentId,
            name: String(node?.name || ""),
            sort: Number(node?.sort || 0),
            level
          });
          walk(node?.children || [], id, level + 1);
        });
      };
      walk(tree);
      return rows;
    },
    collectDescendantIds(targetId) {
      const childrenMap = new Map();
      this.flatList.forEach((item) => {
        const parentId = item?.parent_id === null || item?.parent_id === undefined
          ? null
          : Number(item.parent_id);
        const id = Number(item?.id || 0);
        if (id <= 0 || parentId === null || parentId <= 0) return;
        if (!childrenMap.has(parentId)) {
          childrenMap.set(parentId, []);
        }
        childrenMap.get(parentId).push(id);
      });

      const result = new Set();
      const queue = [Number(targetId)];
      while (queue.length) {
        const current = queue.shift();
        const children = childrenMap.get(current) || [];
        children.forEach((childId) => {
          if (!result.has(childId)) {
            result.add(childId);
            queue.push(childId);
          }
        });
      }
      return result;
    },
    personnelOptionLabel(item) {
      const nickname = String(item?.nickname || "").trim();
      const wechatId = String(item?.wechat_id || "").trim();
      if (nickname && wechatId) {
        return `${nickname}（${wechatId}）`;
      }
      return nickname || wechatId || `#${item?.id || ""}`;
    },
    resetForm() {
      this.form = {
        name: "",
        parent_id: null,
        sort: 0,
        personnel_ids: []
      };
      this.currentEditId = null;
    },
    async openCreateDialog(parentId) {
      this.dialogMode = "create";
      this.resetForm();
      this.form.parent_id = parentId ? Number(parentId) : null;
      this.dialogVisible = true;
      await this.ensurePersonnelOptionsLoaded();
    },
    async openEditDialog(row) {
      this.dialogMode = "edit";
      this.currentEditId = Number(row?.id || 0);
      this.form = {
        name: String(row?.name || ""),
        parent_id: row?.parent_id === null || row?.parent_id === undefined || Number(row?.parent_id) === 0
          ? null
          : Number(row.parent_id),
        sort: Number(row?.sort || 0),
        personnel_ids: Array.isArray(row?.personnel_ids)
          ? row.personnel_ids.map((id) => Number(id)).filter((id) => id > 0)
          : []
      };
      this.dialogVisible = true;
      await this.ensurePersonnelOptionsLoaded();
    },
    async submitDialog() {
      const name = String(this.form.name || "").trim();
      if (!name) {
        this.$message.warning("请填写部门名称");
        return;
      }

      const payload = {
        name,
        parent_id: this.form.parent_id ? Number(this.form.parent_id) : null,
        sort: Number(this.form.sort || 0),
        personnel_ids: Array.from(new Set(
          (Array.isArray(this.form.personnel_ids) ? this.form.personnel_ids : [])
            .map((id) => Number(id))
            .filter((id) => id > 0)
        ))
      };

      this.dialogLoading = true;
      try {
        if (this.dialogMode === "create") {
          await departmentsApi.createDepartment(payload);
          this.$message.success("新增部门成功");
        } else {
          await departmentsApi.updateDepartment(Number(this.currentEditId), payload);
          this.$message.success("编辑部门成功");
        }
        this.dialogVisible = false;
        await this.loadDepartmentTree();
      } catch (e) {
        this.$message.error("保存部门失败，请稍后重试");
      } finally {
        this.dialogLoading = false;
      }
    },
    async removeDepartment(row) {
      const id = Number(row?.id || 0);
      if (id <= 0) return;

      try {
        await this.$confirm(`确认删除部门【${row?.name || id}】吗？`, "提示", {
          type: "warning"
        });
      } catch (e) {
        return;
      }

      this.loading = true;
      try {
        await departmentsApi.deleteDepartment(id);
        this.$message.success("删除部门成功");
        await this.loadDepartmentTree();
      } catch (e) {
        this.$message.error("删除失败，请先删除下级部门");
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.full-height {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 130px);
  overflow: hidden;
}

.table-shell {
  flex: 1;
  min-height: 0;
}

.empty-hint {
  height: 100%;
  border: 1px dashed #cfd8dc;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #607d8b;
  background: #fafcfd;
}

.person-tag {
  margin-right: 6px;
  margin-bottom: 4px;
}

.person-more {
  color: #607d8b;
  font-size: 12px;
}

.field-tips {
  margin-top: 6px;
  color: #64748b;
  font-size: 12px;
}

.loading {
  position: relative;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.76);
  z-index: 10;
}
</style>
